import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface PoweredByBadgeProps {
  /**
   * Whether to show the badge. When false, renders nothing.
   */
  show?: boolean;
  /**
   * Whether this is being displayed in editor/preview mode.
   * When true, shows an "Upgrade to remove" overlay on hover.
   */
  isEditorMode?: boolean;
}

/**
 * A "Powered by CompetePages" badge that appears below the footer
 * for sites on the FREE tier. Links to the marketing site.
 *
 * In editor mode, shows a hover overlay with upgrade CTA.
 * Uses inline styles to make CSS override more difficult.
 */
export const PoweredByBadge: React.FC<PoweredByBadgeProps> = ({
  show = true,
  isEditorMode = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  if (!show) {
    return null;
  }

  const marketingUrl =
    process.env.NODE_ENV === 'production'
      ? 'https://competepages.com'
      : 'http://compete-pages-local.com:5173';

  // Using inline styles to make it harder to remove via CSS
  const containerStyles: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: isEditorMode ? '#f1f5f9' : '#f8fafc',
    borderTop: '1px solid #e2e8f0',
    textAlign: 'center',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: '13px',
    color: isEditorMode ? '#94a3b8' : '#64748b',
    position: 'relative',
  };

  const linkStyles: React.CSSProperties = {
    color: isEditorMode ? '#94a3b8' : '#3b82f6',
    textDecoration: 'none',
    fontWeight: 500,
    transition: 'color 0.2s ease',
    pointerEvents: isEditorMode ? 'none' : 'auto',
  };

  const overlayStyles: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(59, 130, 246, 0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    opacity: isHovered ? 1 : 0,
    transition: 'opacity 0.2s ease',
    pointerEvents: isHovered ? 'auto' : 'none',
  };

  const overlayTextStyles: React.CSSProperties = {
    color: 'white',
    fontSize: '13px',
    fontWeight: 500,
  };

  const upgradeButtonStyles: React.CSSProperties = {
    backgroundColor: 'white',
    color: '#3b82f6',
    padding: '6px 16px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    textDecoration: 'none',
    transition: 'background-color 0.2s ease',
  };

  return (
    <div
      data-powered-by="competepages"
      style={containerStyles}
      onMouseEnter={() => isEditorMode && setIsHovered(true)}
      onMouseLeave={() => isEditorMode && setIsHovered(false)}
    >
      Powered by{' '}
      <a
        href={marketingUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={linkStyles}
        onMouseEnter={e => {
          if (!isEditorMode) {
            e.currentTarget.style.color = '#2563eb';
            e.currentTarget.style.textDecoration = 'underline';
          }
        }}
        onMouseLeave={e => {
          if (!isEditorMode) {
            e.currentTarget.style.color = '#3b82f6';
            e.currentTarget.style.textDecoration = 'none';
          }
        }}
      >
        CompetePages
      </a>
      {/* Editor mode hover overlay */}
      {isEditorMode && (
        <div style={overlayStyles}>
          <span style={overlayTextStyles}>This branding appears on your published site</span>
          <Link
            to="/pricing"
            style={upgradeButtonStyles}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f0f9ff';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'white';
            }}
          >
            Upgrade to Remove
          </Link>
        </div>
      )}
    </div>
  );
};

export default PoweredByBadge;
